// src/pages/Auth.js
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Container, TextField, Button, Typography, Card, CardContent, Grid,
  CircularProgress, MenuItem,
} from "@mui/material";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase";
import "../App.css";

/** ---------- Helpers ---------- */
const countries = [
  "United Kingdom","United States","Canada","Australia","India","Germany","France","Spain","Italy",
  "Netherlands","Brazil","South Africa","Japan","China","Mexico","Russia","United Arab Emirates",
  "Saudi Arabia","Nigeria","Egypt","Pakistan","Bangladesh","Argentina","Other",
];

// Display names used purely for copy in the UI
const planLabel = (planId) => {
  switch (planId) {
    case "free": return "Free Plan";
    case "personal": return "Personal Plan";
    case "family_lite": return "Family Lite Plan";
    case "family": return "Big Family Plan";
    case "school_50": return "Schools – Up to 50 students";
    case "school_100": return "Schools – Up to 100 students";
    case "school_200": return "Schools – Up to 200 students";
    default: return planId || "Free Plan";
  }
};
const isSchoolPlan = (id) => typeof id === "string" && id.startsWith("school_");

// 🔐 Include a honeypot field ("website") to catch bots
const EMPTY_FORM = {
  fullName: "",
  email: "",
  password: "",
  phone: "",
  schoolName: "",
  website: "", // honeypot: should remain empty
};

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const qs = new URLSearchParams(location.search);

  // We keep the plan id only for copy/text; checkout itself happens after auth.
  const selectedPlanId =
    qs.get("plan") || localStorage.getItem("selectedPlan") || "free";
  const selectedBilling =
    (qs.get("billing") || localStorage.getItem("selectedBilling") || "monthly")
      .toLowerCase() === "annual"
      ? "annual"
      : "monthly";

  // Form state
  const [selectedCountry, setSelectedCountry] = useState("");
  const [customCountry, setCustomCountry] = useState("");
  const [formValues, setFormValues] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [verificationHint, setVerificationHint] = useState(""); // UI hint after signup

  // Fresh arrival → clear any old prefill cache
  useEffect(() => {
    setFormValues(EMPTY_FORM);
    setSelectedCountry("");
    setCustomCountry("");
    localStorage.removeItem("preSignup"); // not used in this flow
  }, [selectedPlanId]);

  const handleInputChange = (e) =>
    setFormValues((s) => ({ ...s, [e.target.name]: e.target.value }));

  const handleCountryChange = (e) => {
    const value = e.target.value;
    setSelectedCountry(value);
    if (value !== "Other") setCustomCountry("");
  };

  // Create (or update) the profile doc without touching protected billing/role fields
  const upsertUserProfile = async (uid, emailNorm) => {
    await setDoc(
      doc(db, "users", uid),
      {
        uid,
        fullName: formValues.fullName || "",
        email: emailNorm,
        emailLower: (emailNorm || "").trim().toLowerCase(),
        country: selectedCountry === "Other" ? customCountry : selectedCountry || "",
        phoneNumber: formValues.phone || "",
        schoolName: formValues.schoolName || "",
        createdAt: serverTimestamp(),
      },
      { merge: true }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setVerificationHint("");

    // 🐝 Honeypot check: if the hidden "website" field is filled, likely a bot
    if (formValues.website && formValues.website.trim() !== "") {
      console.warn("[Auth] Honeypot field triggered, blocking submission.");
      alert("Something went wrong. Please try again.");
      return;
    }

    // 🧊 Simple browser-side rate limiting: max 5 attempts in 10 minutes per browser
    try {
      const now = Date.now();
      const raw = localStorage.getItem("signupAttempts") || "[]";
      let attempts = [];
      try {
        attempts = JSON.parse(raw);
        if (!Array.isArray(attempts)) attempts = [];
      } catch {
        attempts = [];
      }
      // keep only last 10 minutes
      attempts = attempts.filter((t) => now - t < 10 * 60 * 1000);

      if (attempts.length >= 5) {
        alert(
          "Too many sign-up attempts from this browser.\n" +
          "Please wait a few minutes and try again."
        );
        return;
      }

      attempts.push(now);
      localStorage.setItem("signupAttempts", JSON.stringify(attempts));
    } catch {
      // if localStorage fails for any reason, silently ignore rate-limit storage
    }

    setLoading(true);

    try {
      const emailNorm = (formValues.email || "").trim().toLowerCase();
      const pass = formValues.password || "";

      if (!emailNorm || !pass) {
        alert("Please enter your email and password.");
        setLoading(false);
        return;
      }

      // Create account or sign in if it already exists
      let user;

      try {
        const cred = await createUserWithEmailAndPassword(auth, emailNorm, pass);
        user = cred.user;
        
      } catch (err) {
        if (err.code === "auth/email-already-in-use") {
          const cred = await signInWithEmailAndPassword(auth, emailNorm, pass);
          user = cred.user;
        } else {
          throw err;
        }
      }

      if (!user) {
        throw new Error("Sign-in failed. Please try again.");
      }

      // TEMP: Email verification disabled (to reduce signup drop-off)
// (Firebase can delay emails / they may land in spam)
setVerificationHint("");

      await user.getIdToken(true);
      await upsertUserProfile(user.uid, emailNorm);

      // If they picked a paid plan on the way in, send them to Subscriptions to start checkout now.
      // Otherwise go straight to dashboard.
      const pickedPaid = selectedPlanId && selectedPlanId !== "free";
      if (pickedPaid) {
        // Preserve their intent so the Subscriptions page can pre-select the price/quantity.
        localStorage.setItem("selectedPlan", selectedPlanId);
        localStorage.setItem("selectedBilling", selectedBilling);
        if (isSchoolPlan(selectedPlanId) && formValues.schoolName) {
          // Optional: if your Subscriptions page needs a starter org name
          localStorage.setItem("intendedSchoolName", formValues.schoolName);
        }
        navigate("/subscriptions");
      } else {
        // NOTE: to fully gate usage by verification, your dashboards / StoryApp
        // should also check auth.currentUser.emailVerified before allowing story generation.
        navigate("/free-dashboard");
      }
    } catch (err) {
      console.error("Auth flow error:", err);
      alert(err?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const bg = require("../assets/images/landing_background.jpg");

  return (
    <div
      style={{
        backgroundImage: `url(${bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        minHeight: "100vh",
        fontFamily: "Montserrat, sans-serif",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <header
        style={{
          width: "100%",
          padding: "10px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.5)",
        }}
      >
        <img
          src={require("../assets/images/logo_name.PNG")}
          alt="YourStoryWorld Logo"
          style={{ height: "40px", cursor: "pointer" }}
          onClick={() => navigate("/")}
        />
        <Button color="inherit" onClick={() => navigate("/help")}>
          Help
        </Button>
      </header>

      <Container maxWidth="sm" style={{ marginTop: 20 }}>
        <Card
          style={{
            padding: 20,
            backgroundColor: "rgba(255,255,255,0.9)",
            borderRadius: 10,
            boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
          }}
        >
          <CardContent>
            <Typography variant="h4" style={{ textAlign: "center", fontWeight: "bold" }}>
              {`${planLabel(selectedPlanId)} — ${
                selectedPlanId === "free" ? "Create account / Sign in" : "Sign in to continue"
              }`}
            </Typography>

            {selectedPlanId !== "free" && (
              <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                After you sign in, you’ll be redirected to start checkout for{" "}
                <strong>{planLabel(selectedPlanId)}</strong> ({selectedBilling}).
              </Typography>
            )}

            <form onSubmit={handleSubmit}>
              {/* 🐝 Honeypot field (hidden from real users, shown to bots) */}
              <input
                type="text"
                name="website"
                value={formValues.website}
                onChange={handleInputChange}
                autoComplete="off"
                tabIndex="-1"
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "-9999px",
                  opacity: 0,
                  height: 0,
                  width: 0,
                  pointerEvents: "none",
                }}
              />

              <Grid container spacing={2} style={{ marginTop: 20 }}>
                {/* Minimal profile capture up front */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="fullName"
                    label="Full Name"
                    value={formValues.fullName}
                    onChange={handleInputChange}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="email"
                    label="Email Address"
                    type="email"
                    value={formValues.email}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="password"
                    label="Password"
                    type="password"
                    value={formValues.password}
                    onChange={handleInputChange}
                    required
                    helperText="If you already have an account, we’ll sign you in."
                  />
                </Grid>

                {/* Optional profile fields for paid plans */}
                {selectedPlanId !== "free" && (
                  <>
                    <Grid item xs={12}>
                      <TextField
                        select
                        fullWidth
                        label="Country"
                        value={selectedCountry}
                        onChange={handleCountryChange}
                      >
                        {countries.map((c, i) => (
                          <MenuItem key={i} value={c}>
                            {c}
                          </MenuItem>
                        ))}
                      </TextField>
                      {selectedCountry === "Other" && (
                        <TextField
                          fullWidth
                          label="Enter Your Country"
                          value={customCountry}
                          onChange={(e) => setCustomCountry(e.target.value)}
                          required
                          style={{ marginTop: 12 }}
                        />
                      )}
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        name="phone"
                        label="Phone Number (Optional)"
                        value={formValues.phone}
                        onChange={handleInputChange}
                      />
                    </Grid>

                    {isSchoolPlan(selectedPlanId) && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          name="schoolName"
                          label="School Name"
                          value={formValues.schoolName}
                          onChange={handleInputChange}
                        />
                      </Grid>
                    )}
                  </>
                )}
              </Grid>

              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={loading}
                style={{ marginTop: 20 }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : selectedPlanId === "free" ? (
                  "Create account / Sign in"
                ) : (
                  "Continue"
                )}
              </Button>

              {/* Small hint about email verification */}
              {verificationHint && (
                <Typography
                  variant="body2"
                  sx={{ mt: 2, fontSize: "0.85rem", color: "#1E3A8A" }}
                >
                  {verificationHint}
                </Typography>
              )}
            </form>
          </CardContent>
        </Card>
      </Container>

      <footer className="page-footer">
        <p>&copy; {new Date().getFullYear()} YourStoryWorld. All rights reserved.</p>
        <nav style={{ marginTop: 10 }}>
          <a href="/about" className="footer-link">About Us</a>
          <a href="/contact" className="footer-link">Contact</a>
        </nav>
      </footer>
    </div>
  );
};

export default AuthPage;
